import RefreshIcon from "@mui/icons-material/Refresh";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useGetBlockedListQuery } from "../../redux/api";
import { describeClient } from "../../utils/common";
import { TABLE_BODY_ROW_MUI_STYLES, TABLE_HEADER_ROW_MUI_STYLES } from "../../utils/constant";
import { Card, PageBody, PageHeader, StatTile } from "./components/SecurityShell";
import { formatDateTime, formatDuration } from "./securityUtils";

const POLL_MS = 30000;

const SimpleTable = ({ headers, rows, emptyText }) => (
    <TableContainer>
        <Table stickyHeader>
            <TableHead>
                <TableRow sx={TABLE_HEADER_ROW_MUI_STYLES}>
                    {headers.map((h) => <TableCell key={h} align="left">{h}</TableCell>)}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.length === 0 ? (
                    <TableRow>
                        <TableCell align="center" colSpan={headers.length} sx={{ py: 5 }}>
                            {emptyText}
                        </TableCell>
                    </TableRow>
                ) : rows.map((cells, i) => (
                    <TableRow key={i} sx={TABLE_BODY_ROW_MUI_STYLES}>
                        {cells.map((c, j) => <TableCell key={j} align="left">{c}</TableCell>)}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const Consumed = ({ entry }) => (
    <Chip
        size="small"
        color={entry.isBlocked ? "error" : "warning"}
        variant="outlined"
        label={`${entry.consumedPoints} / ${entry.allowedPoints}`}
        sx={{ fontWeight: 600 }}
    />
);

const Unblocks = ({ seconds }) =>
    seconds === null || seconds === undefined
        ? <Tooltip title="No expiry recorded for this key" arrow><span>unknown</span></Tooltip>
        : <span>in {formatDuration(seconds)}</span>;

/**
 * Who the rate limiter is currently blocking.
 *
 * There is no blocklist table — `rate-limiter-flexible` keeps one counter key
 * per caller in Redis with the block as its TTL, so this is reconstructed by
 * scanning those keys. Blocks are automatic and self-expiring: there is nothing
 * to unblock here, entries simply disappear when their TTL runs out. The page
 * polls so the countdowns stay honest.
 */
const BlockedList = () => {
    const { data, isLoading, isFetching, refetch } = useGetBlockedListQuery(undefined, {
        pollingInterval: POLL_MS,
    });
    const [showApproaching, setShowApproaching] = useState(false);

    const result = data?.result;
    const blockedIps = result?.blockedIps || [];
    const blockedDevices = result?.blockedDevices || [];
    const approaching = result?.approaching || [];

    if (isLoading) {
        return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
    }

    return (
        <Stack direction="column" gap={2}>
            <PageHeader
                title="Blocked IPs & Devices"
                subtitle="Callers currently being rejected by the rate limiter. Blocks expire on their own."
                actions={
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={refetch}
                        disabled={isFetching}
                    >
                        Refresh
                    </Button>
                }
            />

            <PageBody>
                {result?.redisAvailable === false && (
                    <Alert severity="error">
                        Redis is unreachable, so the block list cannot be read. Note the limiter
                        <strong> fails open</strong> — while Redis is down, requests are being allowed through
                        rather than throttled.
                    </Alert>
                )}

                {result?.truncated && (
                    <Alert severity="warning">
                        The key scan hit its cap — some entries are not shown. This usually means an unusually
                        large amount of traffic is currently being tracked.
                    </Alert>
                )}

                <Stack direction="row" gap={2} flexWrap="wrap">
                    <StatTile
                        label="Blocked IPs"
                        value={blockedIps.length}
                        tone={blockedIps.length ? "error" : "success"}
                    />
                    <StatTile
                        label="Blocked sessions"
                        value={blockedDevices.length}
                        tone={blockedDevices.length ? "error" : "success"}
                        hint="one per device/token"
                    />
                    <StatTile
                        label="Approaching limit"
                        value={approaching.length}
                        tone={approaching.length ? "warning" : "success"}
                        hint="over half their allowance"
                    />
                    <StatTile
                        label="Last scanned"
                        value={result?.scannedAt ? new Date(result.scannedAt).toLocaleTimeString() : "-"}
                        hint={`auto-refreshes every ${POLL_MS / 1000}s`}
                    />
                </Stack>

                <Card>
                    <Box sx={{ p: 2, pb: 1 }}>
                        <Typography variant="h6" fontWeight={700}>Blocked IP addresses</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Anonymous traffic and all login/OTP requests are throttled per IP.
                        </Typography>
                    </Box>
                    <SimpleTable
                        headers={["IP address", "Limiter", "Requests used", "Unblocks", "Key"]}
                        emptyText="No IP addresses are currently blocked."
                        rows={blockedIps.map((e) => [
                            <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{e.identifier}</span>,
                            e.limiterLabel,
                            <Consumed entry={e} />,
                            <Unblocks seconds={e.expiresInSeconds} />,
                            <span style={{ fontFamily: "monospace", fontSize: 12, color: "#888" }}>
                                {e.keyPrefix}:{e.rawKey}
                            </span>,
                        ])}
                    />
                </Card>

                <Card>
                    <Box sx={{ p: 2, pb: 1 }}>
                        <Typography variant="h6" fontWeight={700}>Blocked sessions & devices</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Authenticated traffic is throttled per session token, so a block here affects one
                            device rather than a whole IP.
                        </Typography>
                    </Box>
                    <SimpleTable
                        headers={["User", "Type", "Device", "Device ID", "IP", "Requests used", "Unblocks"]}
                        emptyText="No sessions are currently blocked."
                        rows={blockedDevices.map((e) => [
                            e.resolved
                                ? <span>{e.email || e.phoneNumber || "-"}<br />
                                    <span style={{ color: "#888", fontSize: 12 }}>#{e.userId}</span></span>
                                : <Tooltip title="No live session matches this token — it was revoked or expired while its counter is still running" arrow>
                                    <span style={{ color: "#888" }}>Unresolved</span>
                                </Tooltip>,
                            e.userType || "-",
                            e.resolved ? describeClient(e.userAgent) : "-",
                            <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                                {e.deviceId ? String(e.deviceId).slice(0, 14) + "…" : "-"}
                            </span>,
                            e.ipAddress || "-",
                            <Consumed entry={e} />,
                            <Unblocks seconds={e.expiresInSeconds} />,
                        ])}
                    />
                </Card>

                <Card>
                    <Stack
                        direction="row" alignItems="center" justifyContent="space-between"
                        sx={{ p: 2, cursor: "pointer" }}
                        onClick={() => setShowApproaching((v) => !v)}
                    >
                        <Box>
                            <Typography variant="h6" fontWeight={700}>Approaching the limit</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Not blocked yet, but over half their allowance in the current window.
                            </Typography>
                        </Box>
                        <Button size="small">{showApproaching ? "Hide" : `Show (${approaching.length})`}</Button>
                    </Stack>
                    <Collapse in={showApproaching}>
                        <SimpleTable
                            headers={["Caller", "Kind", "Limiter", "Requests used", "Window resets"]}
                            emptyText="Nobody is close to their limit."
                            rows={approaching.map((e) => [
                                <span style={{ fontFamily: "monospace" }}>
                                    {e.keyType === "session" ? `${String(e.identifier).slice(0, 16)}…` : e.identifier}
                                </span>,
                                e.keyType === "session" ? "Session" : "IP",
                                e.limiterLabel,
                                <Consumed entry={e} />,
                                <Unblocks seconds={e.expiresInSeconds} />,
                            ])}
                        />
                    </Collapse>
                </Card>

                <Typography variant="caption" color="text.secondary">
                    Last scan: {formatDateTime(result?.scannedAt)}. Blocks are stored only in Redis and cannot be
                    lifted manually — they clear themselves when the block duration elapses.
                </Typography>
            </PageBody>
        </Stack>
    );
};

export default BlockedList;
